import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { sermons } from '../sermons_data';


// --- Assets & Constants ---
// Use existing banners or placeholders

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// --- Hero Section ---
const HeroSection = styled.section`
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
`;

const BannerImage = styled.div<{ $bgImage: string; $isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.$bgImage});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  opacity: ${props => (props.$isActive ? 1 : 0)};
  transition: opacity 1.5s ease-in-out;
  z-index: -1;
  background-color: #000; /* Dark background for letterboxing */
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3); 
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  animation: ${fadeIn} 1s ease-out;
  padding: 0 20px;
`;

const HeroScript = styled.h2`
  font-family: 'Nanum Pen Script', cursive; /* Brush script style */
  font-size: clamp(40px, 6vw, 90px);
  margin: 0;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
  font-weight: 400;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(16px, 2.5vw, 24px);
  font-weight: 300;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 0;
  opacity: 0.9;
`;

const HeroInfo = styled.div`
  margin-top: 30px;
  font-size: 18px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.2);
  padding: 10px 30px;
  border-radius: 30px;
  backdrop-filter: blur(5px);
`;

const SliderDots = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  z-index: 10;
`;

const Dot = styled.button<{ $isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => (props.$isActive ? '#fff' : 'rgba(255,255,255,0.4)')};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #fff;
  }
`;

// --- Mission Section ---
const MissionSection = styled.section`
  padding: 100px 0;
  background: #fff;
  text-align: center;
`;

const MissionTitle = styled.h2`
  font-size: 48px;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
  letter-spacing: -1px;

  span {
    color: #2c5aa0;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const MissionText = styled.p`
  font-size: 18px;
  color: #666;
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.8;
  word-break: keep-all;
`;

const ServiceTimeBadge = styled.div`
  display: inline-block;
  border: 1px solid #ddd;
  padding: 10px 25px;
  border-radius: 50px;
  color: #555;
  font-weight: 500;
  font-size: 15px;

  strong {
    color: #2c5aa0;
  }
`;

// --- Container (Reusable) ---
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

// --- Recent Sermons ---
const SermonsSection = styled.section`
  padding-bottom: 100px;
  background: #fff;
`;

const GridValues = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SermonCard = styled.div`
  display: block;
  text-decoration: none;
  cursor: pointer;
  group: hover; /* For transitions */
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ThumbnailBox = styled.div`
  width: 100%;
  height: 300px;
  overflow: hidden;
  border-radius: 12px;
  position: relative;
  margin-bottom: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  /* No play button overlay */
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const SermonInfo = styled.div`
  text-align: left;
`;

const SermonTag = styled.span`
  font-size: 12px;
  color: #2c5aa0;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 5px;
  display: block;
`;

const SermonTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin: 0 0 10px 0;
  line-height: 1.4;
`;

const SermonDate = styled.span`
  font-size: 14px;
  color: #888;
`;

// --- Action Cards ---
const ActionSection = styled.section`
  padding: 0 0 100px;
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionCard = styled(Link) <{ $bg: string }>`
  height: 400px;
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 40px;
  background-image: url(${props => props.$bg});
  background-size: cover;
  background-position: center;

  &::before {
    content: '';
    position: absolute;
    top: 0; 
    left: 0; 
    width: 100%; 
    height: 100%;
    background: linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.1));
    z-index: 1;
    transition: background 0.3s ease;
  }

  &:hover::before {
    background: linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.3));
  }
  
  &:hover button {
    background: #2c5aa0;
    border-color: #2c5aa0;
  }

  /* Content Wrapper */
  > div {
    position: relative;
    z-index: 2;
    color: white;
  }

  h3 {
    font-size: 28px;
    margin: 0 0 10px 0;
    font-weight: 700;
  }

  p {
    font-size: 16px;
    margin: 0 0 25px 0;
    opacity: 0.8;
  }
`;

const ActionButton = styled.button`
  padding: 10px 25px;
  border: 1px solid rgba(255,255,255,0.6);
  background: transparent;
  color: white;
  border-radius: 20px;
  font-size: 14px;
  transition: all 0.3s ease;
  cursor: pointer;
`;

// --- Info & News Grid ---
const InfoNewsSection = styled.section`
  padding-bottom: 100px;
  background: #fafafa;
  padding-top: 80px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBox = styled(Link)`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.03);
  text-decoration: none;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 180px;

  &:hover {
    transform: translateY(-5px);
  }

  h4 {
    font-size: 20px;
    color: #333;
    margin: 0 0 10px 0;
  }

  span {
    font-size: 14px;
    color: #888;
    align-self: flex-start;
  }
  
  .arrow {
    align-self: flex-end;
    color: #ddd;
    font-size: 24px;
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NewsColumn = styled.div``;

const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #333;
  padding-bottom: 15px;
  margin-bottom: 20px;

  h3 {
    font-size: 20px;
    margin: 0;
    font-weight: 700;
  }

  a {
    color: #888;
    font-size: 14px;
    text-decoration: none;
  }
`;

const NewsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NewsItem = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  
  a {
    text-decoration: none;
    color: #444;
    font-size: 15px;
    transition: color 0.2s;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 20px;

    &:hover {
      color: #2c5aa0;
    }
  }

  .date {
    color: #999;
    font-size: 13px;
    white-space: nowrap;
  }
`;

// --- Quick Icons Section ---
const QuickIconsSection = styled.section`
  padding: 60px 0;
  background: #333;
  color: white;
`;

const IconsGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 60px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const IconItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  text-decoration: none;
  color: rgba(255,255,255,0.7);
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    transform: translateY(-5px);
  }
`;

const IconCircle = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  background: rgba(255,255,255,0.05);
  transition: all 0.3s ease;

  ${IconItem}:hover & {
    background: #2c5aa0;
    border-color: #2c5aa0;
  }
`;


const Home: React.FC = () => {
  // State for banners and current index
  const [currentBanner, setCurrentBanner] = useState(0);
  const [banners, setBanners] = useState<string[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const handlePlayClick = (videoId: string) => {
    setSelectedVideo(videoId);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  // Fetch Banners from Backend
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
        const response = await fetch(`${backendUrl}/api/banners`);
        const data = await response.json();

        if (data.success && data.banners && data.banners.length > 0) {
          const bannerUrls = data.banners.map((b: any) => b.fileUrl);
          setBanners(bannerUrls);
        }
      } catch (error) {
        console.error('Failed to fetch banners:', error);
      }
    };

    fetchBanners();
  }, []);

  // Fetch News & Bulletins
  const [news, setNews] = useState<any[]>([]);
  const [bulletins, setBulletins] = useState<any[]>([]);

  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
        const response = await fetch(`${backendUrl}/api/news`);
        const data = await response.json();

        if (data.success && data.news) {
          const newsItems = data.news.filter((item: any) => item.category === 'news').slice(0, 5);
          const bulletinItems = data.news.filter((item: any) => item.category === 'bulletin').slice(0, 5);

          if (newsItems.length > 0) setNews(newsItems);
          if (bulletinItems.length > 0) setBulletins(bulletinItems);
        }
      } catch (error) {
        console.error('Failed to fetch news:', error);
      }
    };

    fetchNewsData();
  }, []);

  // Get recent 2 sermons
  const recentSermons = sermons.slice(0, 2);

  // Rotate Banners
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [banners]);

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        {banners.map((img, index) => (
          <BannerImage
            key={index}
            $bgImage={img}
            $isActive={index === currentBanner}
          />
        ))}
        <HeroContent>
          <HeroSubtitle>Welcome to IJBC</HeroSubtitle>
          <HeroScript>말씀으로 하나님을 경험하는 교회<br />말씀으로 삶을 변화시키는 교회</HeroScript>
          <HeroInfo>
            주일 예배: 오전 11시 / 금요 기도회: 오후 7:30
          </HeroInfo>
        </HeroContent>
        <SliderDots>
          {banners.map((_, idx) => (
            <Dot
              key={idx}
              $isActive={idx === currentBanner}
              onClick={() => setCurrentBanner(idx)}
            />
          ))}
        </SliderDots>
      </HeroSection>

      {/* Mission Section */}
      <MissionSection>
        <Container>
          <MissionTitle>Life Giving <span>Church</span></MissionTitle>
          <MissionText>
            주님의 뜻을 구하여<br />
            주님의 뜻이 이루어지는 교회
          </MissionText>
          <ServiceTimeBadge>
            주일 예배 <strong>오전 11시</strong> · 본당 (Sanctuary)
          </ServiceTimeBadge>
        </Container>
      </MissionSection>

      {/* Recent Sermons */}
      <SermonsSection>
        <Container>
          <GridValues>
            {recentSermons.map((sermon, idx) => (
              <SermonCard
                key={idx}
                onClick={() => handlePlayClick(sermon.videoId)}
              >
                <ThumbnailBox>
                  <img
                    src={`https://img.youtube.com/vi/${sermon.videoId}/hqdefault.jpg`}
                    alt={sermon.title}
                  />
                  {/* Note: Target site has no play button overlay, so we removed it for a cleaner look */}
                </ThumbnailBox>
                <SermonInfo>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <SermonTag>LATEST SERMON</SermonTag>
                    <SermonDate>{sermon.date}</SermonDate>
                  </div>
                  <SermonTitle>{sermon.title}</SermonTitle>
                </SermonInfo>
              </SermonCard>
            ))}
          </GridValues>
        </Container>
      </SermonsSection>

      {/* Action Cards */}
      <ActionSection>
        <Container>
          <ActionGrid>
            <ActionCard to="/sermons" $bg="/previous_sermon.jpg">
              <div>
                <h3>주일 설교</h3>
                <p>지난 주일 말씀을 다시 듣습니다</p>
                <ActionButton>바로 가기 →</ActionButton>
              </div>
            </ActionCard>
            <ActionCard to="/worship" $bg="/worship_directions.jpg">
              <div>
                <h3>예배 안내</h3>
                <p>예배 시간 및 장소 안내</p>
                <ActionButton>바로 가기 →</ActionButton>
              </div>
            </ActionCard>
            <ActionCard to="/greetings" $bg="/new_family.jpg">
              <div>
                <h3>새가족 안내</h3>
                <p>처음 오신 분들을 환영합니다</p>
                <ActionButton>바로 가기 →</ActionButton>
              </div>
            </ActionCard>
          </ActionGrid>
        </Container>
      </ActionSection>

      {/* Info & News Grid */}
      <InfoNewsSection>
        <Container>
          {/* Small Info Boxes */}
          <InfoGrid>
            <InfoBox to="/worship">
              <div>
                <h4>교회소개</h4>
                <span>About Us</span>
              </div>
              <div className="arrow">→</div>
            </InfoBox>
            <InfoBox to="/directions">
              <div>
                <h4>오시는 길</h4>
                <span>Directions</span>
              </div>
              <div className="arrow">→</div>
            </InfoBox>
            <InfoBox to="/news">
              <div>
                <h4>헌금 안내</h4>
                <span>Offering</span>
              </div>
              <div className="arrow">→</div>
            </InfoBox>
          </InfoGrid>

          {/* News Lists */}
          <NewsGrid>
            <NewsColumn>
              <ColumnHeader>
                <h3>교회 소식</h3>
                <Link to="/news">더보기 +</Link>
              </ColumnHeader>
              <NewsList>
                {news.map((item) => (
                  <NewsItem key={item.id}>
                    <Link to={item.link}>{item.title}</Link>
                    <span className="date">{item.date}</span>
                  </NewsItem>
                ))}
              </NewsList>
            </NewsColumn>
            <NewsColumn>
              <ColumnHeader>
                <h3>주보</h3>
                <Link to="/bulletin">더보기 +</Link>
              </ColumnHeader>
              <NewsList>
                {bulletins.map((item) => (
                  <NewsItem key={item.id}>
                    <Link to={item.link}>{item.title}</Link>
                    <span className="date">{item.date}</span>
                  </NewsItem>
                ))}
              </NewsList>
            </NewsColumn>
          </NewsGrid>
        </Container>
      </InfoNewsSection>

      {/* Quick Icons */}
      <QuickIconsSection>
        <Container>
          <IconsGrid>
            <IconItem to="/sermons">
              <IconCircle>📖</IconCircle>
              <span>매일성경</span>
            </IconItem>
            <IconItem to="/community">
              <IconCircle>🙏</IconCircle>
              <span>중보기도</span>
            </IconItem>
            <IconItem to="/worship">
              <IconCircle>💻</IconCircle>
              <span>온라인예배</span>
            </IconItem>
            <IconItem to="/offering">
              <IconCircle>🤍</IconCircle>
              <span>온라인헌금</span>
            </IconItem>
          </IconsGrid>
        </Container>
      </QuickIconsSection>

      {selectedVideo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }} onClick={closeVideo}>
          <div style={{
            position: 'relative',
            width: '90%',
            maxWidth: '900px',
            aspectRatio: '16/9',
            background: '#000'
          }}>
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            <button
              onClick={closeVideo}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-10px',
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: '30px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

    </PageContainer>
  );
};

export default Home;